/**
 * Control+←→（Mac=⌃ / Win=Alt）での roundUnit 調整。
 * 上限: scale 後の小計（scaled）を超えない。超える場合は undefined（呼び出し側でシェイク）。
 * 細刻み: 0 → 1 → 5 → 10 → 15 … / 粗刻み: 0 → 1 → 10 → 100 …
 *
 * 長押し連打: マルチと同型 — すべて開始位置基準で
 *   ±1（次/前の 10×n まで）→ 5（次/前の 100×n まで）→ ×10^n（100…）
 */

/** fine 列（0,1,5,10,15…）の次/前を O(1) で求める */
function getNextFineStep(cur: number): number {
  if (cur < 1) {
    return 1;
  }
  if (cur < 5) {
    return 5;
  }
  if (cur < 10) {
    return 10;
  }
  const snapped = Math.ceil((cur + 0.001) / 5) * 5;
  return snapped <= cur + 0.001 ? snapped + 5 : snapped;
}

function getPrevFineStep(cur: number): number {
  if (cur <= 1) {
    return 0;
  }
  if (cur <= 5) {
    return 1;
  }
  if (cur <= 10) {
    return 5;
  }
  const snapped = Math.floor((cur - 0.001) / 5) * 5;
  return snapped >= cur - 0.001 ? snapped - 5 : snapped;
}

function stepFineGrid(
  cur: number,
  isIncreasing: boolean,
  maxAllowed: number,
): number | undefined {
  const target = isIncreasing ? getNextFineStep(cur) : getPrevFineStep(cur);
  if (isIncreasing) {
    return target <= maxAllowed + 0.001 ? target : undefined;
  }
  return target;
}

function buildCoarseSteps(max: number): number[] {
  const steps = [0, 1, 10];
  let n = 100;
  while (n <= max) {
    steps.push(n);
    n *= 10;
  }
  return steps;
}

function stepInSequence(
  steps: number[],
  cur: number,
  isIncreasing: boolean,
  maxAllowed: number,
): number | undefined {
  if (isIncreasing) {
    const exactIdx = steps.findIndex((s) => s === cur);
    if (exactIdx >= 0 && exactIdx < steps.length - 1) {
      const next = steps[exactIdx + 1];
      return next <= maxAllowed ? next : undefined;
    }
    const next = steps.find((s) => s > cur + 0.001);
    return next !== undefined && next <= maxAllowed ? next : undefined;
  }
  const exactIdx = steps.findIndex((s) => s === cur);
  if (exactIdx > 0) {
    return steps[exactIdx - 1];
  }
  if (cur <= 0) {
    return undefined;
  }
  const prev = [...steps].reverse().find((s) => s < cur - 0.001);
  return prev ?? 0;
}

export function getRoundUnitShiftTarget(
  currentRoundUnit: number | undefined,
  scaledCap: number,
  isIncreasing: boolean,
  coarse: boolean,
): number | undefined {
  const cur = currentRoundUnit ?? 0;
  const maxAllowed = scaledCap > 0 ? scaledCap : 0;
  if (coarse) {
    const steps = buildCoarseSteps(Math.max(maxAllowed, 10));
    return stepInSequence(steps, cur, isIncreasing, maxAllowed);
  }
  return stepFineGrid(cur, isIncreasing, maxAllowed);
}

/** null = クリア、undefined = 上限超過等のエラー */
export function applyRoundUnitShift(
  currentRoundUnit: number | undefined,
  scaledCap: number,
  isIncreasing: boolean,
  coarse: boolean,
): number | null | undefined {
  const target = getRoundUnitShiftTarget(currentRoundUnit, scaledCap, isIncreasing, coarse);
  if (target === undefined) {
    return undefined;
  }
  if (target <= 0) {
    return null;
  }
  return target;
}

/**
 * 開始位置から見た次の 10×n（±1 上限・増加）。
 * 例: 1→10, 10→20, 11→20, 60→70, 65→70。
 */
function getIncrementOneUpperBound(start: number): number | null {
  if (start < 10) {
    return 10;
  }
  if (start % 10 === 0) {
    return start + 10;
  }
  return Math.ceil(start / 10) * 10;
}

/**
 * 開始位置から見た前の 10×n（±1 下限・減少）。
 * 例: 10→1, 20→11, 70→60, 65→60。
 */
function getIncrementOneLowerBound(start: number): number | null {
  if (start <= 1) {
    return null;
  }
  if (start <= 10) {
    return 1;
  }
  if (start % 10 === 0) {
    return start - 10;
  }
  return Math.floor(start / 10) * 10;
}

/** ±1: 開始位置の次/前 10×n まで */
function shouldUseIncrementOne(
  cur: number,
  start: number,
  isIncreasing: boolean,
): boolean {
  if (cur <= 0) {
    return isIncreasing;
  }
  if (isIncreasing) {
    const upper = getIncrementOneUpperBound(start);
    return upper !== null && cur < upper;
  }
  if (cur <= 1) {
    return false;
  }
  const lower = getIncrementOneLowerBound(start);
  const upper = getIncrementOneUpperBound(start);
  return lower !== null && upper !== null && cur > lower && cur <= upper;
}

/**
 * 開始位置から見た次の 100×n（5 刻み上限・増加）。
 * 例: 1→100, 60→100, 350→400, 400→500。
 */
function getIncrementFiveUpperBound(start: number): number | null {
  if (start < 100) {
    return 100;
  }
  if (start % 100 === 0) {
    return start + 100;
  }
  return Math.ceil(start / 100) * 100;
}

/**
 * 開始位置から見た前の 100×n（5 刻み下限・減少）。
 * 例: 100→1, 400→300, 350→300。
 */
function getIncrementFiveLowerBound(start: number): number | null {
  if (start <= 0) {
    return null;
  }
  if (start <= 100) {
    return 1;
  }
  if (start % 100 === 0) {
    return start - 100;
  }
  return Math.floor(start / 100) * 100;
}

/** 5 刻み: 開始位置の次/前 100×n まで（±1 フェーズの外側） */
function shouldUseIncrementFive(
  cur: number,
  start: number,
  isIncreasing: boolean,
): boolean {
  if (cur <= 0) {
    return false;
  }
  if (isIncreasing) {
    const upper = getIncrementFiveUpperBound(start);
    return upper !== null && cur < upper;
  }
  if (cur <= 1) {
    return false;
  }
  const lower = getIncrementFiveLowerBound(start);
  const upper = getIncrementFiveUpperBound(start);
  return lower !== null && upper !== null && cur > lower && cur <= upper;
}

/**
 * 長押し刻み幅（増減とも同じフェーズ順）
 * ±1 → 5（100×n 境界まで）→ ×10^n
 */
export function calculateRoundUnitIncrement(
  currentRoundUnit: number,
  startRoundUnit: number,
  isIncreasing: boolean,
): number {
  const cur = Math.max(0, currentRoundUnit);
  const start = Math.max(0, startRoundUnit);

  if (shouldUseIncrementOne(cur, start, isIncreasing)) {
    return 1;
  }

  if (shouldUseIncrementFive(cur, start, isIncreasing)) {
    return 5;
  }

  if (cur >= 1000) {
    return 1000;
  }
  return 100;
}

/** 加算刻み。null = クリア、undefined = 上限超過 */
export function applyRoundUnitDelta(
  currentRoundUnit: number | undefined,
  scaledCap: number,
  isIncreasing: boolean,
  delta: number,
): number | null | undefined {
  const cur = currentRoundUnit ?? 0;
  const next = isIncreasing ? cur + delta : cur - delta;
  if (next <= 0) {
    return null;
  }
  const maxAllowed = scaledCap > 0 ? scaledCap : 0;
  if (next > maxAllowed + 0.001) {
    return undefined;
  }
  return next;
}

/** 長押し 1 ステップ（±1 は加減算、5 刻み・×10^n はステップ列スナップ） */
export function applyRoundUnitLongPressStep(
  currentRoundUnit: number | undefined,
  startRoundUnit: number,
  scaledCap: number,
  isIncreasing: boolean,
): number | null | undefined {
  const cur = currentRoundUnit ?? 0;
  const increment = calculateRoundUnitIncrement(cur, startRoundUnit, isIncreasing);
  const maxAllowed = scaledCap > 0 ? scaledCap : 0;

  if (increment === 1) {
    return applyRoundUnitDelta(currentRoundUnit, scaledCap, isIncreasing, 1);
  }

  if (increment === 5) {
    const target = stepFineGrid(cur, isIncreasing, maxAllowed);
    if (target === undefined) {
      return undefined;
    }
    if (target <= 0) {
      return null;
    }
    return target;
  }

  const steps = buildCoarseSteps(Math.max(maxAllowed, 100));
  const target = stepInSequence(steps, cur, isIncreasing, maxAllowed);
  if (target === undefined) {
    return undefined;
  }
  if (target <= 0) {
    return null;
  }
  return target;
}
